import { useMemo, useRef, useState, type MouseEvent } from 'react'
import { Button } from '../base/ui.tsx'
import { formatNumber, withUnit } from '../spec/format.ts'
import {
  categories,
  categoryLabels,
  drawnSeries,
  drawOrder,
  MAX_CATEGORY_LABELS,
  seriesStyles,
  valuesAt,
  xDomain,
  yDomain,
  type ChartData,
  type ChartSeries,
  type SeriesStyle,
} from './chart-math.ts'

const W = 560
const H = 236
const PAD = { top: 24, right: 64, bottom: 42, left: 52 }
const PLOT_W = W - PAD.left - PAD.right
const PLOT_H = H - PAD.top - PAD.bottom

const unitOf = (axis: { unit?: string }) => axis.unit ?? ''

/**
 * A line or bar chart: its legend, a hover that reads values out, and (with `table`) the same data as a table. It
 * draws the newest versions (one blue step each); older ones stay in the table.
 */
export function Chart({
  data,
  table = false,
  onShowTable,
}: {
  data: ChartData
  table?: boolean
  onShowTable?: () => void
}) {
  const { drawn, hidden } = useMemo(() => drawnSeries(data.series), [data.series])
  const chart = useMemo(() => ({ ...data, series: drawn }), [data, drawn])
  const styles = useMemo(() => seriesStyles(drawn), [drawn])
  const empty = !data.series.some((s) => s.points.length)
  return (
    <>
      {drawn.length > 1 && !table && (
        <Legend series={drawn} styles={styles} chart={data.chart} hidden={hidden} onTable={onShowTable} />
      )}
      {empty ? (
        <p className="chart-empty">No data yet.</p>
      ) : table ? (
        <DataTable data={data} />
      ) : data.chart === 'line' ? (
        <LineChart data={chart} styles={styles} />
      ) : (
        <BarChart data={chart} styles={styles} />
      )}
    </>
  )
}

/** A chart with its title and description, and a switch between the chart and a table of its data. */
export function ChartPanel({ data, description }: { data: ChartData; description?: string | null }) {
  const [table, setTable] = useState(false)
  return (
    <div className="chart-panel">
      <div className="chart-panel-head">
        {data.title ? <h3 className="chart-panel-title">{data.title}</h3> : <span />}
        <Button variant="ghost" onClick={() => setTable((t) => !t)} aria-pressed={table}>
          {table ? 'Chart' : 'Table'}
        </Button>
      </div>
      {description && <p className="chart-panel-desc">{description}</p>}
      <Chart data={data} table={table} onShowTable={() => setTable(true)} />
    </div>
  )
}

function Legend({
  series,
  styles,
  chart,
  hidden,
  onTable,
}: {
  series: ChartSeries[]
  styles: Map<string, SeriesStyle>
  chart: ChartData['chart']
  hidden: number
  onTable?: (() => void) | undefined
}) {
  return (
    <ul className="chart-legend" aria-label="Series">
      {series.map((s) => {
        const st = styles.get(s.name)!
        return (
          <li key={s.name} data-lead={st.lead || undefined}>
            <svg width="18" height="10" aria-hidden>
              {chart === 'line' ? (
                <line
                  x1="1"
                  x2="17"
                  y1="5"
                  y2="5"
                  stroke={st.color}
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeDasharray={st.dashed ? '4 3' : undefined}
                />
              ) : (
                <rect x="4" y="0" width="10" height="10" rx="2" fill={st.color} />
              )}
            </svg>
            {s.name}
            {s.n != null && <span className="chart-n">n={s.n}</span>}
          </li>
        )
      })}
      {hidden > 0 && (
        <li>
          <button type="button" className="chart-older" onClick={onTable}>
            +{hidden} older {hidden === 1 ? 'version' : 'versions'} in the table
          </button>
        </li>
      )}
    </ul>
  )
}

const label = (data: ChartData) =>
  `${data.title ?? data.y.label}: ${data.y.label} by ${data.x.label}, ${data.series.map((s) => s.name).join(', ')}`

function YAxis({
  ticks,
  y,
  unit,
  label: name,
}: {
  ticks: number[]
  y: (v: number) => number
  unit: string
  label: string
}) {
  return (
    <g className="chart-axis">
      <text x={PAD.left - 8} y={10} textAnchor="start" className="chart-axis-name">
        {name}
        {unit && unit !== '%' ? ` (${unit})` : ''}
      </text>
      {ticks.map((t) => (
        <g key={t}>
          <line className="chart-grid" x1={PAD.left} x2={W - PAD.right} y1={y(t)} y2={y(t)} />
          <text x={PAD.left - 8} y={y(t)} dy="0.32em" textAnchor="end">
            {unit === '%' ? `${formatNumber(t)}%` : formatNumber(t)}
          </text>
        </g>
      ))}
    </g>
  )
}

function LineChart({ data, styles }: { data: ChartData; styles: Map<string, SeriesStyle> }) {
  const { ticks: yTicks, lo, hi } = yDomain(data)
  const xd = xDomain(data.series)
  const x = (v: number) => PAD.left + ((v - xd.lo) / (xd.hi - xd.lo || 1)) * PLOT_W
  const y = (v: number) => PAD.top + (1 - (v - lo) / (hi - lo || 1)) * PLOT_H
  const [hover, setHover] = useState<number | null>(null)
  const svg = useRef<SVGSVGElement>(null)
  const order = drawOrder(data.series, styles)
  const lead = order.at(-1)
  const leadEnd = lead?.points.at(-1)
  const move = (e: MouseEvent<SVGRectElement>) => {
    const box = svg.current!.getBoundingClientRect()
    const px = ((e.clientX - box.left) / box.width) * W
    setHover(xd.lo + ((Math.min(Math.max(px, PAD.left), W - PAD.right) - PAD.left) / PLOT_W) * (xd.hi - xd.lo))
  }
  const at = hover === null ? [] : valuesAt(data.series, hover, styles)
  const atX = at[0]?.x ?? null
  return (
    <div className="chart-plot">
      <svg ref={svg} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={label(data)}>
        <YAxis ticks={yTicks} y={y} unit={unitOf(data.y)} label={data.y.label} />
        <g className="chart-axis">
          {xd.ticks.map((t) => (
            <text key={t} x={x(t)} y={H - PAD.bottom + 16} textAnchor="middle">
              {formatNumber(t)}
            </text>
          ))}
          <text x={PAD.left + PLOT_W / 2} y={H - 6} textAnchor="middle" className="chart-axis-name">
            {data.x.label}
            {data.x.unit && ` (${data.x.unit})`}
          </text>
        </g>
        {atX !== null && <line className="chart-crosshair" x1={x(atX)} x2={x(atX)} y1={PAD.top} y2={H - PAD.bottom} />}
        {order.map((s) => {
          const st = styles.get(s.name)!
          return (
            <polyline
              key={s.name}
              className="chart-line"
              points={s.points.map((p) => `${x(p.x as number)},${y(p.y)}`).join(' ')}
              stroke={st.color}
              strokeWidth={st.lead ? 2.5 : 2}
              strokeDasharray={st.dashed ? '5 4' : undefined}
            />
          )
        })}
        {lead && leadEnd && (
          <text className="chart-end-label" x={x(leadEnd.x as number) + 6} y={y(leadEnd.y)} dy="0.32em">
            {lead.name}
          </text>
        )}
        {at.map((p) => (
          <circle key={p.name} className="chart-dot" cx={x(p.x)} cy={y(p.y)} r={4} fill={styles.get(p.name)!.color} />
        ))}
        <rect
          className="chart-hit"
          x={PAD.left}
          y={PAD.top}
          width={PLOT_W}
          height={PLOT_H}
          onMouseMove={move}
          onMouseLeave={() => setHover(null)}
        />
      </svg>
      {atX !== null && (
        <Tooltip
          at={x(atX) / W}
          heading={`${data.x.label} ${withUnit(atX, unitOf(data.x))}`}
          rows={at.map((p) => ({ name: p.name, value: withUnit(p.y, unitOf(data.y)), style: styles.get(p.name)! }))}
          chart="line"
        />
      )}
    </div>
  )
}

/** A bar with rounded top corners, from the baseline up to `top`. */
function barPath(x: number, top: number, width: number, base: number): string {
  const r = Math.min(4, width / 2, Math.max(0, base - top))
  return `M${x},${base}V${top + r}Q${x},${top} ${x + r},${top}H${x + width - r}Q${x + width},${top} ${x + width},${top + r}V${base}Z`
}

function BarChart({ data, styles }: { data: ChartData; styles: Map<string, SeriesStyle> }) {
  const cats = categories(data.series)
  const names = categoryLabels(cats)
  // Crowded, it labels every nth bar (the hover and the table have them all).
  const every = Math.ceil(cats.length / MAX_CATEGORY_LABELS)
  const { ticks, lo, hi } = yDomain(data)
  const y = (v: number) => PAD.top + (1 - (v - lo) / (hi - lo || 1)) * PLOT_H
  const band = PLOT_W / Math.max(1, cats.length)
  const order = drawOrder(data.series, styles).filter((s) => s.kind !== 'comparison' || data.series.length === 1)
  // Comparisons read as context: a dashed tick across each group rather than another bar.
  const refs = data.series.filter((s) => s.kind === 'comparison' && data.series.length > 1)
  const gap = 2
  const barW = Math.min(30, (band * 0.72 - gap * (order.length - 1)) / Math.max(1, order.length))
  const groupW = barW * order.length + gap * (order.length - 1)
  const [hover, setHover] = useState<number | null>(null)
  const value = (s: ChartSeries, cat: string) => s.points.find((p) => String(p.x) === cat)?.y
  return (
    <div className="chart-plot">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={label(data)}>
        <YAxis ticks={ticks} y={y} unit={unitOf(data.y)} label={data.y.label} />
        {cats.map((cat, i) => {
          const left = PAD.left + band * i + (band - groupW) / 2
          return (
            <g key={cat} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
              <rect
                className="chart-band"
                data-on={hover === i || undefined}
                x={PAD.left + band * i}
                y={PAD.top}
                width={band}
                height={PLOT_H}
              />
              {order.map((s, j) => {
                const v = value(s, cat)
                if (v === undefined) return null
                return (
                  <path
                    key={s.name}
                    d={barPath(left + j * (barW + gap), y(Math.max(v, lo)), barW, y(Math.max(0, lo)))}
                    fill={styles.get(s.name)!.color}
                  />
                )
              })}
              {refs.map((s) => {
                const v = value(s, cat)
                return v === undefined ? null : (
                  <line key={s.name} className="chart-ref" x1={left - 4} x2={left + groupW + 4} y1={y(v)} y2={y(v)} />
                )
              })}
              {i % every === 0 && (
                <text
                  className="chart-cat"
                  x={PAD.left + band * i + band / 2}
                  y={H - PAD.bottom + 16}
                  textAnchor="middle"
                >
                  {names.short(cat)}
                </text>
              )}
            </g>
          )
        })}
        <line
          className="chart-baseline"
          x1={PAD.left}
          x2={W - PAD.right}
          y1={y(Math.max(0, lo))}
          y2={y(Math.max(0, lo))}
        />
        <text x={PAD.left + PLOT_W / 2} y={H - 6} textAnchor="middle" className="chart-axis-name">
          {data.x.label}
          {data.x.unit && ` (${data.x.unit})`}
        </text>
      </svg>
      {hover !== null && (
        <Tooltip
          at={(PAD.left + band * (hover + 1)) / W}
          before={(PAD.left + band * hover) / W}
          heading={names.full(cats[hover]!)}
          rows={data.series.flatMap((s) => {
            const v = value(s, cats[hover]!)
            return v === undefined
              ? []
              : [{ name: s.name, value: withUnit(v, unitOf(data.y)), style: styles.get(s.name)! }]
          })}
          chart="bar"
        />
      )}
    </div>
  )
}

/**
 * Values at the hover, beside it inside the plot: to the right of `at` (a share of the width), or, past the middle,
 * to the left of `before` (defaults to `at`), so it never covers what's hovered.
 */
function Tooltip({
  at,
  before = at,
  heading,
  rows,
  chart,
}: {
  at: number
  before?: number
  heading: string
  rows: { name: string; value: string; style: SeriesStyle }[]
  chart: ChartData['chart']
}) {
  const flip = at > 0.6
  return (
    <div
      className="chart-tip"
      style={flip ? { right: `${(1 - before) * 100}%`, marginRight: 10 } : { left: `${at * 100}%`, marginLeft: 10 }}
      role="status"
    >
      <p className="chart-tip-head">{heading}</p>
      {rows.map((r) => (
        <p key={r.name} className="chart-tip-row">
          <svg width="14" height="8" aria-hidden>
            {chart === 'line' ? (
              <line
                x1="1"
                x2="13"
                y1="4"
                y2="4"
                stroke={r.style.color}
                strokeWidth="2.5"
                strokeDasharray={r.style.dashed ? '3 2' : undefined}
              />
            ) : (
              <rect x="3" y="0" width="8" height="8" rx="2" fill={r.style.color} />
            )}
          </svg>
          <span className="chart-tip-name">{r.name}</span>
          <span className="chart-tip-value">{r.value}</span>
        </p>
      ))}
    </div>
  )
}

/** The same data as a table: a row per x (or category), a column per series (versions oldest first, then the rest). */
function DataTable({ data }: { data: ChartData }) {
  const kindRank = { version: 0, comparison: 1, group: 2 } as const
  const columns = [...data.series].sort(
    (a, b) => kindRank[a.kind ?? 'group'] - kindRank[b.kind ?? 'group'] || (a.version ?? 0) - (b.version ?? 0)
  )
  const xs =
    data.chart === 'bar'
      ? categories(data.series)
      : [...new Set(data.series.flatMap((s) => s.points.map((p) => p.x as number)))].sort((a, b) => a - b)
  const names = categoryLabels(data.chart === 'bar' ? (xs as string[]) : [])
  return (
    <div className="chart-table-wrap">
      <table className="chart-table">
        <thead>
          <tr>
            <th>
              {data.x.label}
              {data.x.unit && ` (${data.x.unit})`}
            </th>
            {columns.map((s) => (
              <th key={s.name} className="chart-num">
                {s.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {xs.map((xv) => (
            <tr key={String(xv)}>
              <td>{typeof xv === 'number' ? formatNumber(xv) : names.full(xv)}</td>
              {columns.map((s) => {
                const p = s.points.find((q) => String(q.x) === String(xv))
                return (
                  <td key={s.name} className="chart-num">
                    {p ? withUnit(p.y, unitOf(data.y)) : '–'}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
